import { Typography, Button } from 'antd';
import { FC, useRef, useEffect } from 'react';
import './index.scss';
import { CustomButton } from '../ui';
import { ROUTES } from '@/types/routes';
import { useAppSelector } from '@/hooks/useAppSelector';
import { selectUser } from '@/slice/user';
import { UserRole } from '@/types';
import { config } from '@/config';
import { isSafeExternalUrl } from '@/helpers/openExternal';
import { ReactComponent as TrustedProIcon } from '@/assets/icons/trusted_pro.svg';

const { Text } = Typography;

interface ProfileMenuOption {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}

interface ProfileDropdownProps {
  visible: boolean;
  onClose: () => void;
  businessName: string;
  userName: string;
  onEditProfile: () => void;
  menuOptions: ProfileMenuOption[];
  userId?: string;
  isTrustedPro?: boolean;
}

const ProfileDropdown: FC<ProfileDropdownProps> = ({
  visible,
  onClose,
  businessName,
  userName,
  onEditProfile,
  menuOptions,
  userId,
  isTrustedPro = false,
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const user = useAppSelector(selectUser);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (visible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [visible, onClose]);

  if (!visible) return null;

  return (
    <>
      <div className="profile-overlay" onClick={onClose} />
      <div className="profile-container" ref={dropdownRef}>
        <div className="profile-header">
          <div className="profile-info">
            <Text className="business-name">{businessName}</Text>
            <Text className="user-name">{userName}</Text>
          </div>
        </div>

        {isTrustedPro && (
          <div className="trusted-pro-card">
            <div className="trusted-pro-card__title">
              <TrustedProIcon className="trusted-pro-card__icon" />
              <span>You&apos;re a Trusted Pro</span>
            </div>
            <p className="trusted-pro-card__desc">
              Your profile is verified and stands out to franchisors.{' '}
              {isSafeExternalUrl(config.VITE_CONSULTANT_PROMO_BANNER_LINK) && (
                <a
                  className="trusted-pro-card__link"
                  href={config.VITE_CONSULTANT_PROMO_BANNER_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Learn more
                </a>
              )}
            </p>
          </div>
        )}

        <div className="edit-profile-section">
          <CustomButton
            type="primary"
            onClick={onEditProfile}
            text="EDIT PROFILE"
          ></CustomButton>
        </div>

        <div className="edit-profile-section">
          <CustomButton
            type="default"
            onClick={() => {
              if (user.role === UserRole.CONSULTANT) {
                window.open(ROUTES.FIND_CONSULTANT + '/' + user.id, '_blank');
              } else {
                window.open(ROUTES.FIND_FRANCHISORS + '/' + userId, '_blank');
              }
              onClose();
            }}
            text="PREVIEW PROFILE"
          ></CustomButton>
        </div>

        <div className="profile-menu">
          {menuOptions.map((option, index) => (
            <div key={index} className="menu-item" onClick={option.onClick}>
              <span className="menu-icon">{option.icon}</span>
              <Text>{option.label}</Text>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export { ProfileDropdown };
